/**
 * Export Service - Exportacion de datos del usuario
 *
 * Permite exportar todos los datos a JSON o CSV.
 * Diseñado para neurodivergencia:
 * - Control total sobre tus datos
 * - Formato legible y reutilizable
 * - Sin rastreo ni telemetria oculta
 */

import { useProtocolStore } from '@/store/protocolStore';
import { useHistoryStore } from '@/store/historyStore';
import { useChecklistStore } from '@/store/checklistStore';
import { useUIStore } from '@/store/uiStore';

interface ExportData {
  metadata: {
    exportDate: string;
    appVersion: string;
    format: 'json' | 'csv';
  };
  protocols: ReturnType<typeof useProtocolStore.getState>['protocols'];
  preferences: ReturnType<typeof useUIStore.getState>['preferences'];
  checklists: ReturnType<typeof useChecklistStore.getState>['checklists'];
  history?: {
    snapshots: unknown[];
    totalDaysTracked: number;
  };
}

/**
 * Recopila todos los datos del usuario de los stores
 */
async function gatherExportData(): Promise<ExportData> {
  const protocolState = useProtocolStore.getState();
  const uiState = useUIStore.getState();
  const checklistState = useChecklistStore.getState();
  const historyState = useHistoryStore.getState();

  // Obtener snapshots del historial (ultimo año)
  const endDate = new Date().toISOString().split('T')[0] ?? '';
  const startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '';
  const snapshots = await historyState.getDaySnapshots(startDate, endDate);

  return {
    metadata: {
      exportDate: new Date().toISOString(),
      appVersion: '2.0.0',
      format: 'json',
    },
    protocols: protocolState.protocols,
    preferences: uiState.preferences,
    checklists: checklistState.checklists,
    history: {
      snapshots,
      totalDaysTracked: snapshots.length,
    },
  };
}

/**
 * Descarga un archivo en el navegador
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Genera nombre de archivo con fecha
 */
function generateFilename(extension: string): string {
  const date = new Date().toISOString().split('T')[0];
  return `exocortex-export-${date}.${extension}`;
}

/**
 * Exporta todos los datos como JSON
 */
export async function exportToJSON(): Promise<void> {
  const data = await gatherExportData();
  data.metadata.format = 'json';

  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, generateFilename('json'), 'application/json');
}

/**
 * Convierte datos a formato CSV
 */
function convertToCSV(data: ExportData): string {
  const lines: string[] = [];

  // Header con metadata
  lines.push('# Exocortex Data Export');
  lines.push(`# Exported: ${data.metadata.exportDate}`);
  lines.push(`# Version: ${data.metadata.appVersion}`);
  lines.push('');

  // Sección: Protocolos
  lines.push('## PROTOCOLS');
  lines.push('id,title,layer,frequency,timeBlock,completedSubtasks,totalSubtasks');
  data.protocols.forEach((p) => {
    const completed = p.subtasks.filter((s) => s.isDone).length;
    const total = p.subtasks.length;
    lines.push(`"${p.id}","${p.title}","${p.layer}","${p.frequency}","${p.timeBlock ?? ''}",${completed},${total}`);
  });
  lines.push('');

  // Sección: Subtareas
  lines.push('## SUBTASKS');
  lines.push('protocolId,subtaskId,label,isDone,estimatedMinutes');
  data.protocols.forEach((p) => {
    p.subtasks.forEach((s) => {
      lines.push(`"${p.id}","${s.id}","${escapeCSV(s.label)}",${s.isDone},${s.estimatedMinutes ?? 0}`);
    });
  });
  lines.push('');

  // Sección: Historial (resumen)
  if (data.history && data.history.snapshots.length > 0) {
    lines.push('## HISTORY SUMMARY');
    lines.push('date,completionRate,protocolsCompleted,totalProtocols');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.history.snapshots.forEach((snapshot: any) => {
      lines.push(`"${snapshot.date}",${snapshot.completionRate ?? 0},${snapshot.completedProtocols ?? 0},${snapshot.totalProtocols ?? 0}`);
    });
    lines.push('');
  }

  // Sección: Preferencias
  lines.push('## PREFERENCES');
  lines.push('setting,value');
  Object.entries(data.preferences).forEach(([key, value]) => {
    lines.push(`"${key}","${String(value)}"`);
  });

  return lines.join('\n');
}

/**
 * Escapa comillas para CSV
 */
function escapeCSV(str: string): string {
  return str.replace(/"/g, '""');
}

/**
 * Exporta todos los datos como CSV
 */
export async function exportToCSV(): Promise<void> {
  const data = await gatherExportData();
  data.metadata.format = 'csv';

  const csvContent = convertToCSV(data);
  downloadFile(csvContent, generateFilename('csv'), 'text/csv');
}

/**
 * Obtiene un resumen de los datos que se exportaran
 */
export async function getExportSummary(): Promise<{
  protocolCount: number;
  subtaskCount: number;
  checklistCount: number;
  historyDays: number;
  estimatedSize: string;
}> {
  const data = await gatherExportData();

  const subtaskCount = data.protocols.reduce(
    (acc, p) => acc + p.subtasks.length,
    0
  );

  // Estimación aproximada del tamaño
  const jsonSize = JSON.stringify(data).length;
  const estimatedSize = jsonSize < 1024
    ? `${jsonSize} bytes`
    : jsonSize < 1024 * 1024
    ? `${(jsonSize / 1024).toFixed(1)} KB`
    : `${(jsonSize / (1024 * 1024)).toFixed(1)} MB`;

  return {
    protocolCount: data.protocols.length,
    subtaskCount,
    checklistCount: data.checklists.length,
    historyDays: data.history?.totalDaysTracked ?? 0,
    estimatedSize,
  };
}
